import { Capacitor } from '@capacitor/core'
import { BiometryType, NativeBiometric } from '@capgo/capacitor-native-biometric'
import { t } from '@lingui/macro'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppState } from '@/capacitor/hooks/useAppState'
import { useCanUseBiometrics } from './useCanUseBiometrics'

/**
 * Hook that provides biometric authentication functionality
 */
export const useBiometrics = () => {
  //-- State --
  const [isAvailable, setIsAvailable] = useState<boolean>(false)
  const [biometryType, setBiometryType] = useState<BiometryType>(BiometryType.NONE)

  // -- Hooks --
  const { active } = useAppState()
  const { canUseBiometrics } = useCanUseBiometrics()

  const biometricTypeDescription = useMemo(() => {
    if (!Capacitor.isNativePlatform()) {
      return ''
    }
    switch (biometryType) {
      case BiometryType.TOUCH_ID: {
        return 'Touch ID'
      }
      case BiometryType.FACE_ID: {
        return 'Face ID'
      }
      case BiometryType.FINGERPRINT: {
        return 'Fingerprint'
      }
      case BiometryType.FACE_AUTHENTICATION: {
        return 'Gezichtsherkenning'
      }
      case BiometryType.IRIS_AUTHENTICATION: {
        return 'Iris scan'
      }
      default: {
        return 'Biometrics'
      }
    }
  }, [biometryType])

  const checkBiometricState = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      return
    }
    const { isAvailable, biometryType } = await NativeBiometric.isAvailable()

    setIsAvailable(isAvailable)
    setBiometryType(biometryType)

    return {
      isAvailable,
      biometryType,
    }
  }, [])

  //-- Effects --

  useEffect(() => {
    if (!active) {
      return
    }
    checkBiometricState()
  }, [active, checkBiometricState])

  //-- Functions --

  /**
   * Request permission to use biometrics
   * On Android, biometrics are always available
   * On iOS, the user is prompted to enable biometrics
   */
  const requestBiometricPermission = async () => {
    // The first time we verify the identity, we skip the canUseBiometrics check
    // This is because we want to show the biometrics permission page to the user
    // And prompt the user to enable biometrics
    return await verifyIdentity(false, true)
  }

  /**
   * Verify the identity of the user using biometrics
   * Prompts the user to authenticate using biometrics
   *
   * @param {boolean} usePinFallback - Whether to allow the user to fallback to using a pin code
   * @returns {boolean} - Whether the user was successfully authenticated
   */
  const verifyIdentity = async (usePinFallback: boolean = true, skipCanUseCheck: boolean = false): Promise<boolean> => {
    if (!isAvailable || !Capacitor.isNativePlatform()) {
      console.error('Biometrics not available')
      return false
    }

    if (!skipCanUseCheck && !canUseBiometrics) {
      console.error('Biometrics are not allowed')
      return false
    }

    // Prompts the user to authenticate using biometrics
    const verified = await NativeBiometric.verifyIdentity({
      reason: t({
        id: 'auth.biometrics.reason',
        message: 'Met {biometric} log je makkelijker in',
        values: { biometric: biometricTypeDescription },
      }),
      title: t({
        id: 'auth.biometrics.title',
        message: 'Schakel {biometric} in om makkelijker in te loggen',
        values: { biometric: biometricTypeDescription },
      }),
      subtitle: t({
        id: 'auth.biometrics.subtitle',
        message: 'Met {biometric} log je makkelijker in',
        values: { biometric: biometricTypeDescription },
      }),
      negativeButtonText: t({
        id: 'auth.biometrics.negativeButtonText',
        message: 'Annuleer',
      }),
      useFallback: usePinFallback,
    })
      .then(() => true)
      .catch(() => false)

    // In the future, we could store the credentials in the keychain or secure storage
    // await NativeBiometric.setCredentials({ username: 'username', password: 'password', server })

    return verified
  }

  return {
    isAvailable,
    biometryType,
    requestBiometricPermission,
    verifyIdentity,
    checkBiometricState: checkBiometricState,
  }
}
